/** @jsx jsx */
import { Fragment, useEffect, useRef } from 'react';
import { jsx } from '@emotion/core';
import { useMediaQuery } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Banner } from 'components';
import ErrorRedirectionView from 'components/ErrorRedirectionView';
import { ROUTES } from 'constants/index';
import { useImgSrc } from 'core/utils/use-img-src.hook';
import { ICustomTheme } from 'interfaces';
import { getSafeHTML } from 'utils';

import { PreviousAnswers } from './PreviousAnswers';
import { QuizForm } from './QuizForm';
import {
  ErrorContainer,
  StyledCard,
  StyledCircularProgress,
  StyledCircularProgressContainer,
  StyledContainer,
  StyledDescription,
  StyledSponsor,
  StyledTitle,
} from './styles';
import { useQuizForm } from './useQuizForm';
import { useUserQuiz, useUserQuizLatestEntry } from './useQuizQueries';
import { getQuizDates } from './utils';

export const QuizPage = (): JSX.Element => {
  const { id: quizIdString } = useParams<{ id: string }>();
  const quizId = Number(quizIdString);

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const isMobile = useMediaQuery((theme: ICustomTheme) =>
    theme.breakpoints.down('sm'),
  );

  const {
    data: quiz,
    isLoading: isLoadingQuiz,
    isError: isQuizError,
    error: quizError,
  } = useUserQuiz(quizId);

  const { data: latestEntry, isLoading: isLoadingLatestEntry } =
    useUserQuizLatestEntry(quizId);
  const { fullUri } = useImgSrc(quiz?.image) || {};
  const quizForm = useQuizForm(quiz ?? ({ formInputGroups: [] } as any));
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (quizForm.isSubmitSuccess) {
      containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [quizForm.isSubmitSuccess]);

  if (isLoadingQuiz || isLoadingLatestEntry) {
    return (
      <StyledCircularProgressContainer>
        <StyledCircularProgress />
      </StyledCircularProgressContainer>
    );
  }

  // Scenario 4 — user is not targeted: the API answered with a 403.
  // Rendered exactly like `ChallengeDeclaration`'s "notTargetedByAction"
  // branch: a full-page `ErrorRedirectionView`, no banner, no card.
  if (isQuizError) {
    const isForbidden = quizError?.response?.status === 403;
    return (
      <ErrorContainer>
        <ErrorRedirectionView
          errorText={t(
            isForbidden ? 'quiz.notTargetedByAction' : 'core.error_occured',
          )}
          actionText={t('core.go_back_home')}
          buttonText={t('menu.home')}
          redirection={ROUTES.HOME}
          isMobile={isMobile}
        />
      </ErrorContainer>
    );
  }

  if (!quiz) {
    return (
      <ErrorContainer>
        <ErrorRedirectionView
          errorText={t('core.error_occured')}
          actionText={t('core.go_back_home')}
          buttonText={t('menu.home')}
          redirection={ROUTES.HOME}
          isMobile={isMobile}
        />
      </ErrorContainer>
    );
  }

  const hasAlreadyAnswered = Boolean(latestEntry);
  const isAllowedToAnswerAgain = !latestEntry || quiz.isMultiAnswerAllowed;

  const dates = getQuizDates(quiz, language);
  const safeDescription = quiz.teaser ? getSafeHTML(quiz.teaser) : '';

  return (
    <Fragment>
      <Banner
        challengeType={'default' as never}
        dates={dates}
        half={false}
        image={fullUri}
        isParticipating
      />

      <StyledContainer ref={containerRef}>
        {/* Description card */}
        <StyledCard>
          <StyledTitle variant="h3">
            <strong>{quiz.title}</strong>
          </StyledTitle>
          <br />
          {!!safeDescription && (
            <StyledDescription
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: safeDescription }}
            />
          )}
          {!!quiz.operationId && (
            <StyledSponsor variant="caption">
              {`#${quiz.operationId}`}
            </StyledSponsor>
          )}
        </StyledCard>

        <QuizForm
          quiz={quiz}
          quizForm={quizForm}
          isAllowedToAnswerAgain={isAllowedToAnswerAgain}
        />

        {hasAlreadyAnswered && (
          <PreviousAnswers
            formValues={latestEntry?.formValues ?? []}
            alreadyAnsweredMessage={t(
              !isAllowedToAnswerAgain
                ? 'quiz.alreadyAnswered'
                : 'quiz.canAnswerAgain',
              { title: quiz.title },
            )}
            showGoHome={!isAllowedToAnswerAgain}
            onGoHome={() => {
              window.location.href = `${ROUTES.BASENAME}${ROUTES.HOME}`;
            }}
            creationDate={latestEntry?.creationDate}
          />
        )}
      </StyledContainer>
    </Fragment>
  );
};

export default QuizPage;
